/**
 * Computes Easter Sunday for a given year using the
 * Meeus/Jones/Butcher algorithm (works for any Gregorian year).
 * Returns a Date object set to Easter Sunday at midnight UTC.
 */
const getEasterDate = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 1-based
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
};

/**
 * Computes Ash Wednesday (Easter minus 46 days).
 */
const getAshWednesday = (year) => {
  const easter = getEasterDate(year);
  const ash = new Date(easter);
  ash.setUTCDate(ash.getUTCDate() - 46);
  return ash;
};

/**
 * Computes the first Sunday of Advent.
 * Advent begins on the Sunday nearest to November 30
 * (the four Sundays before Christmas).
 */
const getAdventStart = (year) => {
  // Find the Sunday on or before November 30
  const nov30 = new Date(Date.UTC(year, 10, 30)); // Nov 30
  const dow = nov30.getUTCDay(); // 0=Sun
  // Nearest Sunday: if dow <= 3 go back dow days, else go forward (7-dow) days
  const offset = dow <= 3 ? -dow : 7 - dow;
  const advent = new Date(nov30);
  advent.setUTCDate(nov30.getUTCDate() + offset);
  return advent;
};

/**
 * Returns a plain date-only comparison object {year,month,day} from
 * a UTC Date so we avoid timezone shifts when comparing.
 */
const toUTC = (date) => ({
  year: date.getUTCFullYear(),
  month: date.getUTCMonth() + 1, // 1-based
  day: date.getUTCDate(),
});

const isBefore = (a, b) =>
  a.year < b.year ||
  (a.year === b.year && a.month < b.month) ||
  (a.year === b.year && a.month === b.month && a.day < b.day);

const isOnOrAfter = (a, b) => !isBefore(a, b);

const isOnOrBefore = (a, b) => !isBefore(b, a);

const addDays = (date, n) => {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() + n);
  return d;
};

export const getCurrentLiturgicalSeason = () => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const today = toUTC(now);

  // --- Key moveable dates for this year ---
  const easter = getEasterDate(year);
  const ashWed = getAshWednesday(year);
  const palmSunday = toUTC(addDays(easter, -7));   // Holy Week begins Palm Sunday
  const holyWeekEnd = toUTC(addDays(easter, -1));  // Holy Saturday
  const easterStart = toUTC(easter);
  const pentecost = toUTC(addDays(easter, 49));    // 50th day (day 0 = Easter)
  const trinitySunday = toUTC(addDays(easter, 56)); // Sunday after Pentecost
  const lentStart = toUTC(ashWed);

  // --- Key fixed dates ---
  const adventStart = toUTC(getAdventStart(year));
  const christmas = { year, month: 12, day: 25 };
  const dec31 = { year, month: 12, day: 31 };
  const jan1 = { year, month: 1, day: 1 };
  const epiphany = { year, month: 1, day: 6 };    // Jan 6

  // ----------------------------------------------------------------
  // Determine current season (evaluated in order of priority)
  // ----------------------------------------------------------------

  // Christmas season: Dec 25 – Jan 5
  const isChristmasThisYear =
    isOnOrAfter(today, christmas) && isOnOrBefore(today, dec31);
  const isChristmasNewYear =
    isOnOrAfter(today, jan1) &&
    isOnOrBefore(today, { year, month: 1, day: 5 });

  if (isChristmasThisYear || isChristmasNewYear) {
    return {
      name: 'Christmas',
      color: '#7a4f00',
      textColor: '#3b2000',        // very dark brown — maximum contrast on gold
      gradient: 'linear-gradient(135deg, #e6a800 0%, #f5bc00 50%, #c98a00 100%)',
    };
  }

  // Advent: Advent Sunday – Dec 24 (current year)
  const adventEnd = { year, month: 12, day: 24 };
  if (
    isOnOrAfter(today, adventStart) &&
    isOnOrBefore(today, adventEnd)
  ) {
    return {
      name: 'Advent',
      color: '#6A0DAD',
      textColor: '#fff',
      gradient: 'linear-gradient(135deg, #4A0080 0%, #6A0DAD 50%, #8B00FF 100%)',
    };
  }

  // Epiphany: Jan 6 – day before Ash Wednesday
  const epiphanyEnd = toUTC(addDays(ashWed, -1));
  if (
    isOnOrAfter(today, epiphany) &&
    isOnOrBefore(today, epiphanyEnd)
  ) {
    return {
      name: 'Epiphany',
      color: '#7a4f00',
      textColor: '#3b2000',
      gradient: 'linear-gradient(135deg, #e6a800 0%, #f5bc00 50%, #c98a00 100%)',
    };
  }

  // Holy Week: Palm Sunday – Holy Saturday
  if (isOnOrAfter(today, palmSunday) && isOnOrBefore(today, holyWeekEnd)) {
    return {
      name: 'Holy Week',
      color: '#8B0000',
      textColor: '#fff',
      gradient: 'linear-gradient(135deg, #4A0000 0%, #8B0000 50%, #DC143C 100%)',
    };
  }

  // Lent: Ash Wednesday – Saturday before Palm Sunday
  if (isOnOrAfter(today, lentStart) && isBefore(today, palmSunday)) {
    return {
      name: 'Lent',
      color: '#6A0DAD',
      textColor: '#fff',
      gradient: 'linear-gradient(135deg, #4A0080 0%, #6A0DAD 50%, #8B00FF 100%)',
    };
  }

  // Pentecost Sunday
  if (
    today.year === pentecost.year &&
    today.month === pentecost.month &&
    today.day === pentecost.day
  ) {
    return {
      name: 'Pentecost',
      color: '#DC143C',
      textColor: '#fff',
      gradient: 'linear-gradient(135deg, #B22222 0%, #DC143C 50%, #FF6347 100%)',
    };
  }

  // Trinity Sunday
  if (
    today.year === trinitySunday.year &&
    today.month === trinitySunday.month &&
    today.day === trinitySunday.day
  ) {
    return {
      name: 'Trinity Sunday',
      color: '#333',
      textColor: '#1a1a1a',
      gradient: 'linear-gradient(135deg, #d0d0d0 0%, #e8e8e8 50%, #c8c8c8 100%)',
    };
  }

  // Eastertide: Easter Sunday – day before Pentecost
  const easterEnd = toUTC(addDays(easter, 48));
  if (isOnOrAfter(today, easterStart) && isOnOrBefore(today, easterEnd)) {
    return {
      name: 'Easter',
      color: '#7a4f00',
      textColor: '#3b2000',
      gradient: 'linear-gradient(135deg, #e6a800 0%, #f5bc00 50%, #c98a00 100%)',
    };
  }

  // Ordinary Time (everything else — after Trinity until Advent)
  return {
    name: 'Ordinary Time',
    color: '#228B22',
    textColor: '#fff',
    gradient: 'linear-gradient(135deg, #006400 0%, #228B22 50%, #32CD32 100%)',
  };
};
